import { ROLES, ROLE_PERMISSIONS, ROLE_NAMES, PERMISSION_DESCRIPTIONS } from './constants';

/**
 * PermissionService - Centralized permission management
 *
 * This service provides methods to check user permissions based on their role.
 * All permission logic is centralized here for easy maintenance and testing.
 */
export class PermissionService {
  /**
   * Check if a user has a specific permission
   * @param userRoleId - The user's role ID
   * @param permission - The permission to check
   * @returns true if user has the permission, false otherwise
   */
  static hasPermission(userRoleId: string, permission: string): boolean {
    if (!userRoleId || !permission) {
      return false;
    }

    const rolePermissions = ROLE_PERMISSIONS[userRoleId] || [];
    return rolePermissions.includes(permission);
  }

  /**
   * Check if a user has any of the specified permissions (OR logic)
   * @param userRoleId - The user's role ID
   * @param permissions - Array of permissions to check
   * @returns true if user has at least one of the permissions, false otherwise
   */
  static hasAnyPermission(userRoleId: string, permissions: string[]): boolean {
    if (!userRoleId || !permissions || permissions.length === 0) {
      return false;
    }

    return permissions.some(permission => this.hasPermission(userRoleId, permission));
  }

  /**
   * Check if a user has all of the specified permissions (AND logic)
   * @param userRoleId - The user's role ID
   * @param permissions - Array of permissions to check
   * @returns true if user has all permissions, false otherwise
   */
  static hasAllPermissions(userRoleId: string, permissions: string[]): boolean {
    if (!userRoleId || !permissions || permissions.length === 0) {
      return false;
    }

    return permissions.every(permission => this.hasPermission(userRoleId, permission));
  }

  /**
   * Get the display name for a role
   * @param roleId - The role ID
   * @returns The display name for the role, or 'Unknown' if not found
   */
  static getRoleName(roleId: string): string {
    return ROLE_NAMES[roleId] || 'Unknown Role';
  }

  /**
   * Get the description for a permission
   * @param permission - The permission key
   * @returns The description for the permission, or empty string if not found
   */
  static getPermissionDescription(permission: string): string {
    return PERMISSION_DESCRIPTIONS[permission] || '';
  }

  /**
   * Get all permissions for a specific role
   * @param roleId - The role ID
   * @returns Array of permission keys for the role
   */
  static getRolePermissions(roleId: string): readonly string[] {
    return ROLE_PERMISSIONS[roleId] || [];
  }

  /**
   * Check if a role exists
   * @param roleId - The role ID to check
   * @returns true if role exists, false otherwise
   */
  static isValidRole(roleId: string): boolean {
    return Object.values(ROLES).includes(roleId as any);
  }

  /**
   * Get all available roles
   * @returns Array of role objects with id and name
   */
  static getAllRoles(): Array<{ id: string; name: string }> {
    return Object.entries(ROLE_NAMES).map(([id, name]) => ({ id, name }));
  }

  /**
   * Check if a user is a primary admin (Level 1)
   * @param userRoleId - The user's role ID
   * @returns true if user is Level 1, false otherwise
   */
  static isPrimaryAdmin(userRoleId: string): boolean {
    return userRoleId === ROLES.LEVEL_1;
  }

  /**
   * Check if a user is a primary admin based on user object
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static isPrimaryAdminFromUser(user: any): boolean {
    return user?.isprimaryadmin === true;
  }

  /**
   * Check if a user has admin-level permissions
   * @param userRoleId - The user's role ID
   * @returns true if user has admin permissions, false otherwise
   */
  static isAdmin(userRoleId: string): boolean {
    return (
      this.isPrimaryAdmin(userRoleId) ||
      this.hasPermission(userRoleId, 'manage_users') ||
      this.hasPermission(userRoleId, 'system_settings')
    );
  }

  /**
   * Check if a user can manage other users
   * @param userRoleId - The user's role ID
   * @returns true if user can manage users, false otherwise
   */
  static canManageUsers(userRoleId: string): boolean {
    return (
      this.hasPermission(userRoleId, 'manage_users') ||
      this.hasPermission(userRoleId, 'create_users') ||
      this.hasPermission(userRoleId, 'edit_users') ||
      this.hasPermission(userRoleId, 'delete_users')
    );
  }

  /**
   * Check if a user can perform estimating tasks
   * @param userRoleId - The user's role ID
   * @returns true if user can perform estimating, false otherwise
   */
  static canPerformEstimating(userRoleId: string): boolean {
    return this.hasAnyPermission(userRoleId, [
      'perform_takeoffs',
      'edit_estimates',
      'create_estimates',
      'view_estimates',
    ]);
  }

  /**
   * Check if a user can manage assemblies and items
   * @param userRoleId - The user's role ID
   * @returns true if user can manage assemblies/items, false otherwise
   */
  static canManageAssemblies(userRoleId: string): boolean {
    return this.hasAnyPermission(userRoleId, [
      'create_assemblies',
      'edit_assemblies',
      'delete_assemblies',
      'create_items',
      'edit_items',
      'delete_items',
    ]);
  }

  /**
   * Check if a user can access closeout features
   * @param userRoleId - The user's role ID
   * @returns true if user can access closeouts, false otherwise
   */
  static canAccessCloseouts(userRoleId: string): boolean {
    return this.hasAnyPermission(userRoleId, ['manage_closeouts', 'view_closeouts']);
  }

  /**
   * Check if a user can use drawing and annotation tools
   * @param userRoleId - The user's role ID
   * @returns true if user can use drawing tools, false otherwise
   */
  static canUseDrawingTools(userRoleId: string): boolean {
    return this.hasAnyPermission(userRoleId, [
      'use_drawing_tools',
      'annotate_documents',
      'edit_annotations',
      'delete_annotations',
    ]);
  }

  // PRIMARY ADMIN ONLY METHODS
  // These methods check for primary admin status specifically

  /**
   * Check if a user can manage other users (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canManageOtherUsers(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can create new users (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canCreateUsers(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can delete other users (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canDeleteUsers(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can change other users' roles (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canChangeUserRoles(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can activate/deactivate other users (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canActivateDeactivateUsers(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can manage system settings (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canManageSystemSettings(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can manage subscriptions (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canManageSubscriptions(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can access analytics (primary admin only)
   * @param user - The user object
   * @returns true if user is primary admin, false otherwise
   */
  static canAccessAnalytics(user: any): boolean {
    return this.isPrimaryAdminFromUser(user);
  }

  /**
   * Check if a user can edit their own primary admin status (never allowed)
   * @param user - The user object
   * @returns false - users cannot edit their own primary admin status
   */
  static canEditOwnPrimaryAdminStatus(user: any): boolean {
    return false; // Never allow users to edit their own primary admin status
  }
}
